import React from "react";
import { Listbox, Transition } from "@headlessui/react";
import {
  Bars3BottomLeftIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { Outlet } from "react-router-dom";
import { SearchContext } from "../context/search";
import NavigationBigScreen from "../../navigation/components/bigscreen";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { classNames } from "../utils/classnames";
import { MlsContext } from "../../mls/context/mls";
import NavigationSmallScreen from "../../navigation/components/smallscreen";
import CommonNotification from "./notification";
import Clipboard from "./clipboard";
import { ApplicationIcon } from "./application";

const Layout: React.FC = () => {
  const { search, setSearch } = React.useContext(SearchContext);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const { mls, setMls, mlses } = React.useContext(MlsContext);

  const handleSearchChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setSearch(event.target.value),
    [setSearch]
  );

  return (
    <>
      <div>
        <NavigationBigScreen />
        <NavigationSmallScreen
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />

        <div className="md:pl-64 flex flex-col flex-1">
          <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white shadow">
            <button
              type="button"
              className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zenlist-500 md:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3BottomLeftIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            <div className="flex-1 px-4 flex justify-between">
              <div className="grow flex">
                <label htmlFor="search-field" className="sr-only">
                  Search
                </label>
                <div className="relative grow text-gray-400 focus-within:text-gray-600">
                  <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon
                      className="h-5 w-5"
                      aria-hidden="true"
                    />
                  </div>
                  <input
                    id="search-field"
                    className="block w-full h-full pl-8 pr-3 py-2 border-transparent text-gray-900 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-0 focus:border-transparent sm:text-sm"
                    placeholder="Search for accounts, agents, groups, identities, offices"
                    type="search"
                    name="search"
                    value={search}
                    onChange={handleSearchChange}
                  />
                </div>
                <div className="relative text-gray-400 focus-within:text-gray-600 flex itesm-center w-40">
                  <Listbox value={mls} onChange={setMls}>
                    {({ open }) => (
                      <div className="flex items-center w-full">
                        <div className="mt-1 relative w-full">
                          <Listbox.Button className="bg-white relative w-full pl-2 pr-10 py-1 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-sm rounded-md border border-gray-300 shadow-sm">
                            <span className="block truncate">
                              {mls ? (<span><ApplicationIcon application={mls.application} /> {mls.shortName}</span>) : <span>All</span>}
                            </span>
                            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                              <ChevronUpDownIcon
                                className="h-5 w-5 text-gray-400"
                                aria-hidden="true"
                              />
                            </span>
                          </Listbox.Button>
                          <Transition
                            show={open}
                            as={React.Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <Listbox.Options className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                              <Listbox.Option
                                className={({ active }) =>
                                  classNames(
                                    active
                                      ? "text-white bg-indigo-600"
                                      : "text-gray-900",
                                    "cursor-default select-none relative py-2 pl-3 pr-9"
                                  )
                                }
                                value={undefined}
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span
                                      className={classNames(
                                        selected
                                          ? "font-semibold"
                                          : "font-normal",
                                        "block truncate"
                                      )}
                                    >
                                      All
                                    </span>

                                    {selected ? (
                                      <span
                                        className={classNames(
                                          active
                                            ? "text-white"
                                            : "text-indigo-600",
                                          "absolute inset-y-0 right-0 flex items-center pr-4"
                                        )}
                                      >
                                        <CheckIcon
                                          className="h-5 w-5"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>

                              {mlses.map((mls) => (
                                <Listbox.Option
                                  key={mls.id}
                                  className={({ active }) =>
                                    classNames(
                                      active
                                        ? "text-white bg-indigo-600"
                                        : "text-gray-900",
                                      "cursor-default select-none relative py-2 pl-3 pr-9"
                                    )
                                  }
                                  value={mls}
                                >
                                  {({ selected, active }) => (
                                    <>
                                      <span
                                        className={classNames(
                                          selected
                                            ? "font-semibold"
                                            : "font-normal",
                                          "block truncate"
                                        )}
                                      >
                                        <ApplicationIcon application={mls.application} /> {mls.shortName}
                                      </span>

                                      {selected ? (
                                        <span
                                          className={classNames(
                                            active
                                              ? "text-white"
                                              : "text-indigo-600",
                                            "absolute inset-y-0 right-0 flex items-center pr-4"
                                          )}
                                        >
                                          <CheckIcon
                                            className="h-5 w-5"
                                            aria-hidden="true"
                                          />
                                        </span>
                                      ) : null}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </Transition>
                        </div>
                      </div>
                    )}
                  </Listbox>
                </div>
              </div>
            </div>
          </div>

          <main>
            <Outlet />
          </main>
        </div>
      </div>

      <CommonNotification />
      <Clipboard />
    </>
  );
};

export default Layout;
